/**
 * Preset column mappings for Stripe and Event Espresso CSV imports.
 * Keys are our internal field names; values are candidate CSV header substrings.
 */

export const STRIPE_PAYMENTS_PRESET = {
  name: 'Stripe Payments (All)',
  source: 'stripe',
  type: 'payment',
  hasRefundColumn: true, // Amount Refunded is on the same row
  columnHints: {
    email: ['customer_email (metadata)', 'customer email', 'email', 'customer_email'],
    amountCents: ['amount'],
    amountRefunded: ['amount refunded', 'amount_refunded'],
    feeCents: ['fee'],
    date: ['created date (utc)', 'created date', 'created'],
    stripePaymentId: ['id'],
    statusOrType: ['status'],
    description: ['description'],
    subEventLabel: ['description'],
  },
  amountIsCents: false, // Stripe Dashboard exports in dollars (e.g. 150.00)
};

export const EVENT_ESPRESSO_PRESET = {
  name: 'Event Espresso Registrations',
  source: 'event_espresso',
  type: 'payment',
  allowZeroAmount: true,
  columnHints: {
    email: ['email address', 'email'],
    amountCents: ["registration's share of the transaction total", "registration's share", 'amount paid', 'amount'],
    date: ['time registration occurred', 'payment date(s)', 'date'],
    stripePaymentId: ['gateway transaction id(s)', 'gateway transaction id'],
    eeTransactionId: ['transaction id'],
    eeRegistrationId: ['registration id'],
    subEventLabel: ['ticket name', 'ticket'],
    statusOrType: ['registration status'],
    description: ['ticket datetimes'],
  },
  amountIsCents: false,
};

/**
 * Ticket categories for status label derivation.
 * - registrationFees: not activities, show "Paid" instead of "Registered"
 * - registrationFeesUseActiveFreshlingCount: expected qty = all active freshlings (not manifest)
 *   Weekend Only uses manifest count; "Registration (Required)" uses active freshling count
 * - activityTickets: tracked via manifestTickets re-import
 * - futureManifest: will be hooked to manifest systems later
 */
export const TICKET_CATEGORIES = {
  registrationFees: new Set(['Weekend Only', 'Registration (Required)']),
  /** Expected qty = active freshlings. Weekend Only is NOT included (uses manifest). */
  registrationFeesUseActiveFreshlingCount: new Set(['Registration (Required)']),
  activityTickets: new Set([
    'Canopy Thursday', 'Canopy Friday', 'Fury Lab', 'After Party',
    'Water Training (Thur)', 'Water Training (Fri)', 'Sky Curious',
  ]),
  futureManifest: new Set(['Saturday Competition', 'High Altitude', 'Jet']),
};

/** Saturday Competition = 4 jumps per person; used for prorating used/remain. High Altitude / Jet = 1 per ticket. */
export const USAGE_TRACKED_JUMPS_PER_TICKET = {
  'Saturday Competition': 4,
  'High Altitude': 1,
  'Jet': 1,
};

/**
 * Map raw ticket/event name from EE to our canonical subEventLabel.
 * Mirrors eventNameToSummaryKey from functions/index.js.
 */
export function mapTicketNameToSubEventLabel(raw) {
  if (!raw || typeof raw !== 'string') return raw || '';
  const e = raw.toLowerCase().replace(/_/g, ' ').trim();
  if (!e) return raw;

  const iso = raw.match(/(\d{4}-\d{2}-\d{2})/);
  let dow = null;
  if (iso) {
    const y = parseInt(iso[1].slice(0, 4), 10);
    const mo = parseInt(iso[1].slice(5, 7), 10) - 1;
    const d = parseInt(iso[1].slice(8, 10), 10);
    const dt = new Date(Date.UTC(y, mo, d, 12, 0, 0));
    if (!Number.isNaN(dt.getTime())) dow = dt.getUTCDay();
  }

  if (e.includes('saturday') && e.includes('competition')) return 'Saturday Competition';
  if (e.includes('fury lab') || e.includes('tunnel')) return 'Fury Lab';
  if (e.includes('after party')) return 'After Party';
  if (e.includes('water training')) {
    if (e.includes('friday') || e.includes('fri)') || dow === 5) return 'Water Training (Fri)';
    if (e.includes('thursday') || e.includes('thur') || e.includes('thu)') || dow === 4) return 'Water Training (Thur)';
    if (dow === 5) return 'Water Training (Fri)';
    if (dow === 4) return 'Water Training (Thur)';
    return 'Water Training (Thur)';
  }
  if (e.includes('sky curious')) return 'Sky Curious';
  if (e.includes('gear request')) return 'Gear Requests';
  if (e.includes('high altitude')) return 'High Altitude';
  if (e.includes('jet') || e.includes('jump the jet')) return 'Jet';
  if (e.includes('canopy')) {
    if (e.includes('friday') || e.includes('fri)') || dow === 5) return 'Canopy Friday';
    if (e.includes('thursday') || e.includes('thur') || e.includes('thu)') || dow === 4) return 'Canopy Thursday';
    if (dow === 5) return 'Canopy Friday';
    if (dow === 4) return 'Canopy Thursday';
    return 'Canopy Thursday';
  }
  if ((e.includes('weekend only') || e.includes('weekend-only')) &&
      (e.includes('registration') || e.includes('required'))) return 'Weekend Only';
  if (e.includes('add-ons only') || e.includes('add-ons')) return 'Add-Ons';

  // Stripe Description patterns (e.g. "Fresh Meet 2026", "Fresh Meet 2026 Weekend Only")
  if (e.includes('fresh meet') && e.includes('weekend only')) return 'Weekend Only';
  if (e.includes('fresh meet') && e.includes('add-ons')) return 'Add-Ons';
  if (e.includes('fresh meet')) return 'Fresh Meet 2026';

  return raw; // Return as-is if no match
}
